import { useMemo, useState } from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { CreateHeaders, textHeader } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';
import WebkuaTable from '../webkua-table/webkua-table';
import type { AssetReconciliationResult } from './sales-force-asset-reconciliation';
import type { ContractReconciliationResult } from './sales-force-contract-reconciliation';
import { ReconciliationLink, ReconciliationLinks, salesforceRecordUrl } from './reconciliation-links';
import { salesforceCalendarDate, validateSalesforceInternal } from './salesforce-internal-validation';
import type { SalesforceInternalFinding, SalesforceInternalSeverity } from './salesforce-internal-validation';
import { ReconciliationFilterSelect } from './reconciliation-filter-select';

export function SalesforceInternalValidation({ assets, contracts }: {
  assets: AssetReconciliationResult; contracts: ContractReconciliationResult;
}) {
  const { t } = useTranslation();
  const [severity, setSeverity] = useState('');
  const [rule, setRule] = useState('');
  const sourceAssets = assets.salesforceAssets;
  const sourceContracts = contracts.salesforceContracts;
  const hasEvidence = Array.isArray(sourceAssets) && Array.isArray(sourceContracts)
    && !!salesforceCalendarDate(contracts.comparedAt) && !!salesforceCalendarDate(assets.comparedAt);
  const findings = useMemo(() => hasEvidence && sourceAssets && sourceContracts
    ? validateSalesforceInternal(sourceAssets, sourceContracts, contracts.comparedAt) : [],
  [hasEvidence, sourceAssets, sourceContracts, contracts.comparedAt]);
  const text = (key: string) => String(t(`salesforceInternal.${key}`));
  if (!hasEvidence) return <Alert severity="error">{text('missingEvidence')}</Alert>;
  const severities: SalesforceInternalSeverity[] = ['error', 'warning', 'unevaluable'];
  const rules = [...new Set(findings.map((finding) => finding.rule))].sort();
  const severityFindings = findings.filter((finding) => !severity || finding.severity === severity);
  const ruleCounts = new Map<string, number>();
  for (const finding of severityFindings)
    ruleCounts.set(finding.rule, (ruleCounts.get(finding.rule) ?? 0) + 1);
  const rows = severityFindings.filter((finding) => !rule || finding.rule === rule).map((finding) => ({
    source: finding,
    severity: text(`severity.${finding.severity}`),
    rule: text(`rules.${finding.rule}`),
    company: finding.asset?.companyNumber ?? finding.contract?.companyNumber ?? '-',
    contractNumber: finding.contract?.contractNumber ?? finding.asset?.contractNumber ?? '-',
    asset: finding.relatedAssets.map((asset) => asset.name ?? asset.id ?? '-').join(', ') || '-',
    productCode: [...new Set(finding.relatedAssets.map((asset) => asset.productCode ?? '-'))].join(', ') || '-',
    assetStatus: [...new Set(finding.relatedAssets.map((asset) => asset.status ?? '-'))].join(', ') || '-',
    contractStatus: finding.contract?.status ?? '-',
    installDate: [...new Set(finding.relatedAssets.map((asset) => asset.installDate?.slice(0, 10) ?? '-'))].join(', ') || '-',
    contractStart: finding.contract?.startDate?.slice(0, 10) ?? '-',
    usageEnd: [...new Set(finding.relatedAssets.map((asset) => asset.usageEndDate?.slice(0, 10) ?? '-'))].join(', ') || '-',
    quantity: finding.relatedAssets.map((asset) => asset.quantity ?? '-').join(', ') || '-',
    cancellation: [...new Set(finding.relatedAssets.map((asset) => asset.cancellationDate?.slice(0, 10) ?? '-'))].join(', ') || '-',
    contractEnd: finding.contract?.terminationDate?.slice(0, 10) ?? '-',
    renewal: finding.contract?.nextRenewalDate?.slice(0, 10) ?? '-',
    actual: finding.actual ?? '-',
    expected: finding.rule === 'InconsistentAssetEndDates' ? text('sameEndDate') : finding.expected ?? '-',
  }));
  return <Stack spacing={2} sx={{ mt: 2 }} data-testid="salesforce-internal-results">
    <Alert severity="info">{text('scope')}</Alert>
    <Typography variant="caption">{t('salesforceInternal.snapshots', {
      assets: assets.comparedAt, contracts: contracts.comparedAt,
    })}</Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {severities.map((value) => <Chip key={value}
        label={`${text(`severity.${value}`)}: ${findings.filter((finding) => finding.severity === value).length}`} />)}
    </Stack>
    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
      <ReconciliationFilterSelect label={text('severityFilter')} value={severity} onChange={setSeverity}
        options={[{ value: '', label: text('all') },
          ...severities.map((value) => ({ value, label: text(`severity.${value}`) }))]} />
      <ReconciliationFilterSelect label={text('ruleFilter')} value={rule} onChange={setRule}
        options={[{ value: '', label: text('all'), count: severityFindings.length },
          ...rules.map((value) => ({ value, label: text(`rules.${value}`), count: ruleCounts.get(value) ?? 0 }))]} />
    </Stack>
    {findings.length === 0 && <Alert severity="info">{text('noFindings')}</Alert>}
    <Box>
      <WebkuaTable data={rows} showNoDataAlert={false} exportColumnKeys={[
        'severity', 'rule', 'company', 'contractNumber', 'asset', 'productCode', 'assetStatus',
        'contractStatus', 'installDate', 'contractStart', 'usageEnd', 'quantity', 'cancellation', 'contractEnd', 'renewal', 'actual', 'expected',
      ]} headers={CreateHeaders([
        'severity', 'rule', 'company', 'contractNumber', 'asset', 'productCode', 'assetStatus',
        'contractStatus', 'installDate', 'contractStart', 'usageEnd', 'quantity', 'cancellation', 'contractEnd', 'renewal', 'actual', 'expected',
      ].map((field) => textHeader(field, text(`columns.${field}`)))).map((header) => ({
        ...header,
        ...(header.field === 'contractNumber' ? { cellRenderer: ({ data }: { data?: { source: SalesforceInternalFinding } }) =>
          data && <ReconciliationLink label={data.source.contract?.contractNumber ?? data.source.asset?.contractNumber ?? '-'}
            href={data.source.contract?.id ? salesforceRecordUrl('Contract', data.source.contract.id)
              : data.source.asset?.contractId ? salesforceRecordUrl('Contract', data.source.asset.contractId) : null} /> } : {}),
        ...(header.field === 'asset' ? { cellRenderer: ({ data }: { data?: { source: SalesforceInternalFinding } }) =>
          data && (data.source.relatedAssets.length > 0 ? <ReconciliationLinks links={data.source.relatedAssets.map((asset) => ({
            label: asset.name ?? asset.id ?? '-', href: asset.id ? salesforceRecordUrl('Asset', asset.id) : null,
          }))} /> : '-') } : {}),
        ...(header.field === 'company' ? { cellRenderer: ({ data }: { data?: { source: SalesforceInternalFinding } }) =>
          data && <ReconciliationLink label={data.source.asset?.companyNumber ?? data.source.contract?.companyNumber ?? '-'}
            href={data.source.asset?.accountId ? salesforceRecordUrl('Account', data.source.asset.accountId)
              : data.source.contract?.accountId ? salesforceRecordUrl('Account', data.source.contract.accountId) : null} /> } : {}),
      }))} />
    </Box>
  </Stack>;
}
